import React, { useState, useEffect } from 'react';
import { Save, X, Info } from 'lucide-react';

const TankFormModal = ({ show, onClose, onSave, tank }) => {
  const [formData, setFormData] = useState({
    tankID: '',
    tankName: '',
    deviceID: '',
    targetTemp: 55,
    displayOrder: 0,
    notes: '',
    // New MQTT-specific configuration fields
    mqttTopics: {
      command: '', // Topic for sending commands
      sensor: '',  // Topic for receiving sensor data
      status: ''   // Topic for receiving status updates
    }
  });

  useEffect(() => {
    if (tank) {
      setFormData({
        tankID: tank.tankID || '',
        tankName: tank.tankName || '',
        deviceID: tank.deviceID || '',
        targetTemp: tank.targetTemp || 55,
        displayOrder: tank.displayOrder || 0,
        notes: tank.notes || '',
        mqttTopics: {
          command: tank.mqttTopics?.command || `winery/${tank.deviceID}/cmnd/+`,
          sensor: tank.mqttTopics?.sensor || `winery/${tank.deviceID}/tele/SENSOR`,
          status: tank.mqttTopics?.status || `winery/${tank.deviceID}/stat/+`
        }
      });
    } else {
      // Reset to default values for new tank
      setFormData({
        tankID: '',
        tankName: '',
        deviceID: '',
        targetTemp: 55,
        displayOrder: 0,
        notes: '',
        mqttTopics: {
          command: '',
          sensor: '',
          status: ''
        }
      });
    }
  }, [tank, show]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare data for saving
    const saveData = {
      ...formData,
      // Ensure deviceID is always prefixed with 'winery/'
      deviceID: formData.deviceID.replace(/^winery\//, ''),
      // Include MQTT topics in the saved data
      mqttTopics: formData.mqttTopics
    };

    onSave(saveData);
  };

  const handleTopicChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      mqttTopics: {
        ...prev.mqttTopics,
        [type]: value
      }
    }));
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{tank ? 'Edit Tank' : 'Add New Tank'}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Tank Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.tankName}
                      onChange={(e) => setFormData({ ...formData, tankName: e.target.value })}
                      required
                      placeholder="e.g. Syrah Tank 1"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Device ID</label>
                    <div className="input-group">
                      <span className="input-group-text">winery/</span>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.deviceID.replace(/^winery\//, '')}
                        onChange={(e) => setFormData({ ...formData, deviceID: e.target.value })}
                        required
                        placeholder="tank1, tank2, etc."
                      />
                    </div>
                    <div className="form-text">Unique identifier for this tank's MQTT device</div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Target Temperature (°F)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.targetTemp}
                      onChange={(e) => setFormData({ ...formData, targetTemp: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) })}
                    />
                    <div className="form-text">Lower numbers appear first</div>
                  </div>
                </div>

                {/* MQTT Topics Configuration */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0 d-flex align-items-center">
                      <Info size={16} className="me-2" />
                      MQTT Topic Configuration
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Command Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.command}
                          onChange={(e) => handleTopicChange('command', e.target.value)}
                          placeholder="winery/tank1/cmnd/+"
                        />
                        <div className="form-text">Topic for sending commands</div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Sensor Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.sensor}
                          onChange={(e) => handleTopicChange('sensor', e.target.value)}
                          placeholder="winery/tank1/tele/SENSOR"
                        />
                        <div className="form-text">Topic for receiving sensor data</div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Status Topic</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.mqttTopics.status}
                          onChange={(e) => handleTopicChange('status', e.target.value)}
                          placeholder="winery/tank1/stat/+"
                        />
                        <div className="form-text">Topic for receiving status updates</div>
                      </div>
                    </div>
                    <div className="alert alert-info py-2">
                      <small>
                        <Info size={14} className="me-1" />
                        Wildcards (+) can be used to listen to multiple subtopics. The device ID will be dynamically replaced.
                      </small>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                  ></textarea>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    <X size={16} className="me-1" />
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <Save size={16} className="me-1" />
                    {tank ? 'Update Tank' : 'Add Tank'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TankFormModal;